import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Tag, Button } from 'antd';
import { 
  ShoppingCartOutlined, EyeOutlined,
  HeartOutlined, HeartFilled,
  FireOutlined, GiftOutlined, PercentageOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { formatImageUrl } from '../../services/api';
import { useInView } from 'react-intersection-observer';
import './PromotionSlider.css';

const { Title, Text } = Typography;

// Componente de Medalla de Descuento mejorado
const DiscountBadge = ({ value }) => {
  return (
    <motion.div 
      className="promo-discount-badge"
      initial={{ scale: 0, rotate: -10 }}
      animate={{ scale: 1, rotate: 0 }}
      whileHover={{ scale: 1.05, rotate: -5 }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}
    >
      <div className="discount-inner">
        <span className="discount-value">-{value}%</span>
      </div>
    </motion.div>
  );
};

// Indicadores de progreso elegantes
const ProgressIndicators = ({ current, total, onChange }) => {
  return (
    <div className="progress-indicators">
      {Array.from({ length: total }).map((_, i) => (
        <motion.button
          key={i}
          className={`indicator-dot ${current === i ? 'active' : ''}`}
          onClick={() => onChange(i)}
          initial={false}
          animate={{ 
            width: current === i ? 24 : 8,
            opacity: current === i ? 1 : 0.6,
            background: current === i ? 
              'linear-gradient(90deg, #ff6da0, #9c27b0)' : 
              'rgba(233, 30, 99, 0.2)'
          }}
          whileHover={{ 
            scale: 1.2, 
            opacity: 1,
            background: 'linear-gradient(90deg, #ff6da0, #9c27b0)'
          }}
          whileTap={{ scale: 0.9 }}
          aria-label={`Ir a promoción ${i + 1}`}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        />
      ))}
    </div>
  );
};

// Botones de navegación estilizados
const NavigationButtons = ({ onPrev, onNext }) => {
  const prevBtnControls = useAnimation();
  const nextBtnControls = useAnimation();

  const pulseEffect = async (controls) => {
    await controls.start({
      scale: [1, 1.05, 1],
      transition: { duration: 0.4 }
    });
  };

  return (
    <>
      <motion.button 
        className="nav-button prev-button"
        onClick={() => { pulseEffect(prevBtnControls); onPrev(); }}
        whileHover={{ 
          scale: 1.1, 
          boxShadow: '0 10px 25px rgba(255, 109, 160, 0.4)' 
        }}
        whileTap={{ scale: 0.95 }}
        animate={prevBtnControls}
        aria-label="Promoción anterior"
      >
        <motion.div 
          animate={{ x: [0, -5, 0] }} 
          transition={{ repeat: Infinity, repeatType: "mirror", duration: 1.5 }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 6L9 12L15 18" />
          </svg>
        </motion.div>
      </motion.button>
      
      <motion.button 
        className="nav-button next-button"
        onClick={() => { pulseEffect(nextBtnControls); onNext(); }}
        whileHover={{ 
          scale: 1.1, 
          boxShadow: '0 10px 25px rgba(255, 109, 160, 0.4)' 
        }}
        whileTap={{ scale: 0.95 }}
        animate={nextBtnControls}
        aria-label="Siguiente promoción"
      >
        <motion.div 
          animate={{ x: [0, 5, 0] }} 
          transition={{ repeat: Infinity, repeatType: "mirror", duration: 1.5 }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6L15 12L9 18" />
          </svg>
        </motion.div>
      </motion.button>
    </>
  );
};

// Contador de slider elegante
const SlideCounter = ({ current, total }) => {
  return (
    <div className="slide-counter">
      <motion.span
        key={current}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        className="current"
      >
        {current + 1}
      </motion.span>
      <span className="separator">/</span>
      <span className="total">{total}</span>
    </div>
  );
};

// Barra de progreso para la transición automática
const AutoplayProgressBar = ({ isPlaying, duration = 5000, progress }) => {
  return (
    <div className="autoplay-progress-container">
      {isPlaying && (
        <motion.div 
          className="autoplay-progress-bar"
          style={{ width: `${progress}%` }}
        />
      )}
    </div>
  );
};

// Componente de título para el slider
const SliderTitle = ({ title = "Promociones Destacadas" }) => {
  return (
    <div className="promotion-slider-title">
      <h2>{title}</h2>
    </div>
  );
};

const PromotionSlider = ({ products = [], addToCart, title = "Promociones Destacadas" }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true); // Siempre auto-reproducir
  const [autoplayProgress, setAutoplayProgress] = useState(0);
  const [favorites, setFavorites] = useState(new Set());
  const autoPlayRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const touchStartX = useRef(0);
  
  // Detector de intersección para pausar/reanudar cuando no es visible
  const { ref: containerRef, inView } = useInView({
    threshold: 0.3,
    triggerOnce: false
  });
  
  // Verificar si hay productos disponibles
  const hasProducts = products && products.length > 0;
  
  // Función para navegar entre slides
  const goToSlide = useCallback((index, dir) => {
    // Feedback táctil
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
    
    setDirection(dir);
    
    if (typeof index === 'number') {
      setCurrentIndex(index);
    } else {
      setCurrentIndex(prev => {
        if (dir > 0) {
          return prev === products.length - 1 ? 0 : prev + 1;
        } else {
          return prev === 0 ? products.length - 1 : prev - 1;
        }
      });
    }
    
    // Reiniciar el progreso
    setAutoplayProgress(0);
  }, [products.length]);
  
  const handlePrev = () => goToSlide(undefined, -1);
  const handleNext = () => goToSlide(undefined, 1);
  
  // Control de autoplay con progreso visible - mejorado para transiciones automáticas constantes
  useEffect(() => {
    if (!hasProducts || !inView) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      return;
    }
    
    // Gestión del progreso
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    const autoplayDuration = 4000; // 4 segundos para transiciones más rápidas
    const progressStep = 100 / (autoplayDuration / 100); // Incremento cada 100ms
    setAutoplayProgress(0);
    
    progressIntervalRef.current = setInterval(() => {
      setAutoplayProgress(prev => {
        if (prev >= 100) return 100;
        return prev + progressStep;
      });
    }, 100);
    
    // Autoplay
    autoPlayRef.current = setTimeout(() => {
      goToSlide(undefined, 1);
    }, autoplayDuration);
    
    return () => {
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [hasProducts, inView, goToSlide, currentIndex]);
  
  // Detección de gestos táctiles
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    // Detección de deslizamiento con umbral
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };
  
  // Manejar favoritos con animación
  const toggleFavorite = (e, productId) => {
    e.stopPropagation();
    
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(productId)) {
        newFavorites.delete(productId);
      } else {
        newFavorites.add(productId);
      }
      return newFavorites;
    });
  };
  
  // Manejar añadir al carrito con efecto visual
  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    addToCart(product, 1);
  };
  
  // No renderizar si no hay productos
  if (!hasProducts) return null;
  
  // Obtener el producto actual
  const currentProduct = products[currentIndex];
  const promotion = currentProduct.promotions?.[0];
  
  // Calcular precio con descuento
  const discountedPrice = promotion?.type === 'descuento' 
    ? currentProduct.price * (1 - promotion.value / 100) 
    : currentProduct.price;
  
  // Variantes para animaciones
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '50%' : '-50%',
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.4 }
      }
    },
    exit: (direction) => ({
      x: direction > 0 ? '-50%' : '50%',
      opacity: 0,
      scale: 0.95,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.2 }
      }
    })
  };

  return (
    <div className="elegant-promotion-slider-container" ref={containerRef}>
      {/* Título del Slider */}
      <SliderTitle title={title} />
      
      <div 
        className="promotion-content"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Contador de slides */}
        <SlideCounter current={currentIndex} total={products.length} />
        
        {/* Indicadores de progreso */}
        <ProgressIndicators 
          current={currentIndex} 
          total={products.length}
          onChange={(index) => goToSlide(index, index > currentIndex ? 1 : -1)}
        />
        
        {/* Barra de progreso de autoplay siempre visible */}
        <AutoplayProgressBar 
          isPlaying={inView} 
          progress={autoplayProgress}
        />
        
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={currentIndex}
            className="promotion-slide"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            {/* Fondo limpio y adaptable */}
            <div className="clean-bg"></div>
            
            <div className="promotion-layout adaptive-layout">
              {/* Panel de información con mejor contraste */}
              <motion.div 
                className="promotion-info info-panel"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <div className="info-panel-content">
                  {/* Jerarquía de información mejorada */}
                  
                  {/* Etiqueta de promoción y categoría en misma línea */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '12px' }}>
                    {/* Etiqueta de promoción con mejor contraste */}
                    {promotion && (
                      <motion.div 
                        className="promotion-tag contrast-tag"
                        initial={{ opacity: 0, y: -20, scale: 0.8 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.3, type: 'spring' }}
                      >
                        {promotion.type === 'descuento' ? (
                          <>
                            <PercentageOutlined className="tag-icon" /> 
                            <span className="tag-text">{promotion.value}% DESCUENTO</span>
                          </>
                        ) : (
                          <>
                            <GiftOutlined className="tag-icon" /> 
                            <span className="tag-text">OFERTA ESPECIAL</span>
                          </>
                        )}
                      </motion.div>
                    )}
                  
                    {/* Categoría con mejor visibilidad */}
                    <motion.div 
                      className="product-category"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                    >
                      <Tag className="category-tag">{currentProduct.categoryName}</Tag>
                    </motion.div>
                  </div>
                  
                  {/* Título del producto más legible */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="title-container"
                  >
                    <Title level={2} className="high-contrast-title">
                      {currentProduct.name}
                    </Title>
                  </motion.div>
                  
                  {/* Precios con mejor contraste */}
                  <motion.div 
                    className="contrast-pricing"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                  >
                    {promotion && promotion.type === 'descuento' && (
                      <Text className="original-price">{currentProduct.price.toFixed(2)} Bs.</Text>
                    )}
                    <motion.div className="current-price-container">
                      <motion.span 
                        className="current-price"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                      >
                        {discountedPrice.toFixed(2)} Bs.
                      </motion.span>
                      
                      {promotion && promotion.type === 'descuento' && (
                        <motion.span 
                          className="savings-label"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.8 }}
                        >
                          <PercentageOutlined /> 
                          Ahorras <span className="savings-amount">
                            {(currentProduct.price - discountedPrice).toFixed(2)} Bs.
                          </span>
                        </motion.span>
                      )}
                    </motion.div>
                  </motion.div>
                  
                  {/* Alerta de stock mejor destacada */}
                  {currentProduct.stock <= 5 && currentProduct.stock > 0 && (
                    <motion.div 
                      className="enhanced-warning"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ 
                        opacity: 1, 
                        scale: 1,
                      }}
                      transition={{ delay: 0.9, duration: 0.5 }}
                    >
                      <motion.div
                        animate={{ 
                          scale: [1, 1.2, 1],
                          rotate: [0, 5, 0, -5, 0],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          repeatType: "reverse"
                        }}
                        className="pulse-icon"
                      >
                        <FireOutlined />
                      </motion.div>
                      <span>¡Solo quedan <b>{currentProduct.stock}</b> unidades!</span>
                    </motion.div>
                  )}
                  
                  {/* Acciones con mejor visibilidad */}
                  <motion.div 
                    className="enhanced-actions"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                  >
                    <Button
                      type="default"
                      className="action-btn enhanced-view-btn"
                      onClick={() => navigate(`/product/${currentProduct.id}`)}
                      icon={<EyeOutlined />}
                      size="large"
                    >
                      Ver detalles
                    </Button>
                    
                    {currentProduct.stock > 0 && (
                      <Button
                        type="primary"
                        className="action-btn enhanced-cart-btn"
                        onClick={(e) => handleAddToCart(e, currentProduct)}
                        icon={<ShoppingCartOutlined />}
                        size="large"
                      >
                        <span className="cart-btn-text">Añadir al carrito</span>
                      </Button>
                    )}
                    
                    <motion.button 
                      className={`enhanced-favorite-btn ${favorites.has(currentProduct.id) ? 'active' : ''}`}
                      onClick={(e) => toggleFavorite(e, currentProduct.id)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      aria-label={favorites.has(currentProduct.id) ? "Quitar de favoritos" : "Añadir a favoritos"}
                    >
                      {favorites.has(currentProduct.id) ? <HeartFilled /> : <HeartOutlined />}
                    </motion.button>
                  </motion.div>
                </div>
              </motion.div>
              
              {/* Panel de imagen adaptable al tamaño real */}
              <motion.div 
                className="promotion-image adaptive-image"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <motion.div 
                  className="product-image-container adaptive-container"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ 
                    duration: 6, 
                    repeat: Infinity, 
                    repeatType: "reverse",
                    ease: "easeInOut" 
                  }}
                >
                  <motion.img 
                    src={formatImageUrl(currentProduct.image)} 
                    alt={currentProduct.name} 
                    className="product-image adaptive-product-image"
                    loading="lazy"
                    whileHover={{ scale: 1.03 }}
                    transition={{ duration: 0.3 }}
                    onLoad={(e) => {
                      // Optimizar la visualización basada en las proporciones de la imagen
                      const img = e.target;
                      const container = img.parentElement;
                      const ratio = img.naturalWidth / img.naturalHeight;
                      
                      if (ratio < 0.8) {
                        container.classList.add('portrait-image');
                      } else if (ratio > 1.2) {
                        container.classList.add('landscape-image');
                      } else {
                        container.classList.add('square-image');
                      }
                    }}
                  />
                  
                  {/* Badge de descuento */}
                  {promotion && promotion.type === 'descuento' && promotion.value > 0 && (
                    <DiscountBadge value={promotion.value} />
                  )}
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PromotionSlider;
